import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Lock, Loader2, Tag, Truck, Store } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SHIPPING_RATE = 9.95;

export const Checkout = () => {
  const { cart, getCartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [deliveryMethod, setDeliveryMethod] = useState('shipping');
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    street: '',
    suburb: '',
    state: '',
    postcode: '',
    country: 'Australia'
  });

  const [errors, setErrors] = useState({});

  // Load discount from sessionStorage
  useEffect(() => {
    const savedDiscount = sessionStorage.getItem('goat_discount');
    if (savedDiscount) {
      const parsed = JSON.parse(savedDiscount);
      // Recalculate discount based on current subtotal
      const subtotal = getCartTotal();
      if (parsed.code === 'GOAT10') {
        parsed.amount = Math.round(subtotal * 0.1 * 100) / 100;
      }
      setAppliedDiscount(parsed);
    }
  }, [cart]);

  const subtotal = getCartTotal();
  const discountAmount = appliedDiscount ? appliedDiscount.amount : 0;
  const shippingCost = deliveryMethod === 'shipping' ? SHIPPING_RATE : 0;
  const total = subtotal - discountAmount + shippingCost;

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email';
    
    // Only validate shipping address if shipping is selected
    if (deliveryMethod === 'shipping') {
      if (!formData.street.trim()) newErrors.street = 'Street address is required';
      if (!formData.suburb.trim()) newErrors.suburb = 'Suburb is required';
      if (!formData.state.trim()) newErrors.state = 'State is required';
      if (!formData.postcode.trim()) newErrors.postcode = 'Postcode is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setLoading(true);

    try {
      // Validate stock availability first
      const stockValidation = await axios.post(`${API}/orders/validate-stock`, 
        cart.map(item => ({ productId: item.productId, quantity: item.quantity }))
      );
      
    } catch (stockError) {
      // Handle stock validation errors
      if (stockError.response?.data?.detail?.errors) {
        const errors = stockError.response.data.detail.errors;
        const errorMessages = errors.map(e => e.error).join('. ');
        toast.error(`Stock issue: ${errorMessages}`);
        setLoading(false);
        return;
      }
    }

    try {
      // Create order with delivery method and discount info
      const orderData = {
        customerFirstName: formData.firstName,
        customerLastName: formData.lastName,
        customerEmail: formData.email,
        customerPhone: formData.phone || null,
        deliveryMethod: deliveryMethod,
        shippingStreet: deliveryMethod === 'shipping' ? formData.street : null,
        shippingSuburb: deliveryMethod === 'shipping' ? formData.suburb : null,
        shippingState: deliveryMethod === 'shipping' ? formData.state : null,
        shippingPostcode: deliveryMethod === 'shipping' ? formData.postcode : null,
        shippingCountry: deliveryMethod === 'shipping' ? formData.country : null,
        items: cart.map(item => ({
          productId: item.productId,
          productName: item.productName,
          size: item.size,
          colour: item.colour,
          quantity: item.quantity,
          price: item.price
        })),
        appliedDiscountCode: appliedDiscount?.code || null,
        discountAmount: discountAmount
      };

      const orderResponse = await axios.post(`${API}/orders`, orderData);
      const order = orderResponse.data;

      // Create Stripe checkout session (uses the discounted total from order)
      const checkoutResponse = await axios.post(`${API}/checkout/create-session`, {
        orderId: order.id,
        originUrl: window.location.origin
      });

      // Clear discount from session storage after successful order creation
      sessionStorage.removeItem('goat_discount');

      // Redirect to Stripe
      if (checkoutResponse.data.url) {
        window.location.href = checkoutResponse.data.url;
      } else {
        throw new Error('No checkout URL received');
      }

    } catch (error) {
      console.error('Checkout error:', error);
      // Handle various error response formats
      let errorMessage = 'Checkout failed. Please try again.';
      const detail = error.response?.data?.detail;
      if (detail) {
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // Pydantic validation errors come as an array
          errorMessage = detail.map(err => err.msg || err.message || JSON.stringify(err)).join('. ');
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || detail.message || JSON.stringify(detail);
        }
      }
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-white py-24 text-center">
        <div className="max-w-md mx-auto px-6">
          <h1 className="font-heading font-black text-2xl uppercase mb-4">Your Cart is Empty</h1>
          <p className="text-zinc-500 mb-8">Add some items before checking out.</p>
          <Link to="/shop" className="btn-primary inline-block">
            Go to Shop
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-[#09090b] text-white py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link 
            to="/cart" 
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors text-sm mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Cart
          </Link>
          <h1 
            className="font-heading font-black text-3xl md:text-4xl uppercase tracking-tight"
            data-testid="checkout-title"
          >
            Checkout
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Customer Details */}
            <div className="lg:col-span-2 space-y-8">
              {/* Contact Info */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
              >
                <h2 className="font-heading font-bold text-xl uppercase tracking-tight mb-6">
                  Contact Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName">First Name *</Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className={`mt-1 h-12 ${errors.firstName ? 'border-red-500' : ''}`}
                      data-testid="input-firstName"
                    />
                    {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>}
                  </div>
                  <div>
                    <Label htmlFor="lastName">Last Name *</Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className={`mt-1 h-12 ${errors.lastName ? 'border-red-500' : ''}`}
                      data-testid="input-lastName"
                    />
                    {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>}
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`mt-1 h-12 ${errors.email ? 'border-red-500' : ''}`}
                      data-testid="input-email"
                    />
                    {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone (Optional)</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="mt-1 h-12"
                      data-testid="input-phone"
                    />
                  </div>
                </div>
              </motion.div>

              {/* Delivery Method */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.05 }}
              >
                <h2 className="font-heading font-bold text-xl uppercase tracking-tight mb-6">
                  Delivery Method
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Ship to Address */}
                  <button
                    type="button"
                    onClick={() => setDeliveryMethod('shipping')}
                    className={`p-4 border-2 text-left transition-all ${
                      deliveryMethod === 'shipping'
                        ? 'border-[#00ff9d] bg-[#00ff9d]/5'
                        : 'border-zinc-200 hover:border-zinc-300'
                    }`}
                    data-testid="delivery-shipping"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        deliveryMethod === 'shipping' ? 'border-[#00ff9d]' : 'border-zinc-300'
                      }`}>
                        {deliveryMethod === 'shipping' && (
                          <div className="w-2.5 h-2.5 rounded-full bg-[#00ff9d]" />
                        )}
                      </div>
                      <Truck className={`w-5 h-5 ${deliveryMethod === 'shipping' ? 'text-[#00cc7d]' : 'text-zinc-400'}`} />
                      <div>
                        <p className="font-bold text-sm">Ship to Address</p>
                        <p className="text-zinc-500 text-xs mt-0.5">${SHIPPING_RATE.toFixed(2)} flat rate</p>
                      </div>
                    </div>
                  </button>

                  {/* Local Pickup */}
                  <button
                    type="button"
                    onClick={() => setDeliveryMethod('pickup')}
                    className={`p-4 border-2 text-left transition-all ${
                      deliveryMethod === 'pickup'
                        ? 'border-[#00ff9d] bg-[#00ff9d]/5'
                        : 'border-zinc-200 hover:border-zinc-300'
                    }`}
                    data-testid="delivery-pickup"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        deliveryMethod === 'pickup' ? 'border-[#00ff9d]' : 'border-zinc-300'
                      }`}>
                        {deliveryMethod === 'pickup' && (
                          <div className="w-2.5 h-2.5 rounded-full bg-[#00ff9d]" />
                        )}
                      </div>
                      <Store className={`w-5 h-5 ${deliveryMethod === 'pickup' ? 'text-[#00cc7d]' : 'text-zinc-400'}`} />
                      <div>
                        <p className="font-bold text-sm">Local Pickup</p>
                        <p className="text-zinc-500 text-xs mt-0.5">FREE - arranged separately</p>
                      </div>
                    </div>
                  </button>
                </div>
                
                {deliveryMethod === 'pickup' && (
                  <div className="mt-4 p-4 bg-zinc-50 border border-zinc-200 text-sm text-zinc-600">
                    <p>Pickup details will be sent to your email after order confirmation.</p>
                  </div>
                )}
              </motion.div>

              {/* Shipping Address - Only show if shipping is selected */}
              {deliveryMethod === 'shipping' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 }}
                >
                  <h2 className="font-heading font-bold text-xl uppercase tracking-tight mb-6">
                    Shipping Address
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="street">Street Address *</Label>
                      <Input
                        id="street"
                        name="street"
                        value={formData.street}
                        onChange={handleInputChange}
                        className={`mt-1 h-12 ${errors.street ? 'border-red-500' : ''}`}
                        data-testid="input-street"
                      />
                      {errors.street && <p className="text-red-500 text-sm mt-1">{errors.street}</p>}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="suburb">Suburb *</Label>
                        <Input
                          id="suburb"
                          name="suburb"
                          value={formData.suburb}
                          onChange={handleInputChange}
                          className={`mt-1 h-12 ${errors.suburb ? 'border-red-500' : ''}`}
                          data-testid="input-suburb"
                        />
                        {errors.suburb && <p className="text-red-500 text-sm mt-1">{errors.suburb}</p>}
                      </div>
                      <div>
                        <Label htmlFor="state">State *</Label>
                        <Input
                          id="state"
                          name="state"
                          value={formData.state}
                          onChange={handleInputChange}
                          placeholder="e.g. NSW, VIC, QLD"
                          className={`mt-1 h-12 ${errors.state ? 'border-red-500' : ''}`}
                          data-testid="input-state"
                        />
                        {errors.state && <p className="text-red-500 text-sm mt-1">{errors.state}</p>}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="postcode">Postcode *</Label>
                        <Input
                          id="postcode"
                          name="postcode"
                          value={formData.postcode}
                          onChange={handleInputChange}
                          className={`mt-1 h-12 ${errors.postcode ? 'border-red-500' : ''}`}
                          data-testid="input-postcode"
                        />
                        {errors.postcode && <p className="text-red-500 text-sm mt-1">{errors.postcode}</p>}
                      </div>
                      <div>
                        <Label htmlFor="country">Country</Label>
                        <Input
                          id="country"
                          name="country"
                          value={formData.country}
                          disabled
                          className="mt-1 h-12 bg-zinc-100"
                          data-testid="input-country"
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.2 }}
                className="bg-zinc-50 p-6 md:p-8 sticky top-24"
                data-testid="checkout-summary"
              >
                <h2 className="font-heading font-bold text-xl uppercase tracking-tight mb-6">
                  Order Summary
                </h2>

                {/* Items */}
                <div className="space-y-4 max-h-64 overflow-y-auto mb-6">
                  {cart.map(item => (
                    <div 
                      key={`${item.productId}-${item.size}-${item.colour}`}
                      className="flex gap-3"
                    >
                      <div className="w-16 h-16 bg-zinc-200 shrink-0 overflow-hidden">
                        <img
                          src={item.imageUrl}
                          alt={item.productName}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{item.productName}</p>
                        <p className="text-zinc-500 text-xs">
                          {item.size && `${item.size}`}
                          {item.size && item.colour && ' / '}
                          {item.colour && `${item.colour}`}
                          {' × '}{item.quantity}
                        </p>
                        <p className="font-medium text-sm mt-1">
                          ${(item.price * item.quantity).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Totals */}
                <div className="border-t border-zinc-200 pt-4 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-600">Subtotal</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>
                  
                  {appliedDiscount && (
                    <div className="flex justify-between text-sm text-[#00cc7d]" data-testid="checkout-discount">
                      <span className="flex items-center gap-1">
                        <Tag className="w-3 h-3" />
                        Discount ({appliedDiscount.code})
                      </span>
                      <span>-${discountAmount.toFixed(2)}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-600">
                      {deliveryMethod === 'shipping' ? 'Shipping' : 'Shipping (Local Pickup)'}
                    </span>
                    <span className={shippingCost === 0 ? 'text-[#00cc7d] font-medium' : ''}>
                      {shippingCost === 0 ? 'FREE' : `$${shippingCost.toFixed(2)}`}
                    </span>
                  </div>
                  <div className="flex justify-between font-bold text-lg pt-2 border-t border-zinc-200">
                    <span>Total</span>
                    <span data-testid="checkout-total">
                      ${total.toFixed(2)} <span className="text-zinc-400 text-sm font-normal">AUD</span>
                    </span>
                  </div>
                </div>

                {/* Pay Button */}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-14 mt-6 bg-[#09090b] text-white hover:bg-[#00ff9d] hover:text-black font-bold uppercase tracking-wider rounded-full transition-all disabled:opacity-50"
                  data-testid="pay-now-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      Pay ${total.toFixed(2)} AUD
                    </>
                  )}
                </Button>

                <p className="text-zinc-500 text-xs text-center mt-4 flex items-center justify-center gap-1">
                  <Lock className="w-3 h-3" />
                  Secure checkout powered by Stripe
                </p>
              </motion.div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
